import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useCallback } from "react";
import { toast } from "sonner";
import { blogCreationService } from "../services/blogCreationService";
import { isPublishCancelledError } from "../lib/prePublishGate";
import { generateHTMLFromRTE } from "../components/blog-templates";
import { useTenantStore } from "../stores/useTenantStore";
import type {
	Content,
	CreateContentData,
	ContentFilters,
	ContentStage,
} from "../components/BlogCreationCommandCenter/types";
import type { SchemaPreviewResponse } from "../services/blogCreationService";

// Query key constants
const QUERY_KEYS = {
	content: "blog-creation-content",
	contentItem: "blog-creation-content-item",
	contentStatus: "blog-creation-content-status",
};

/**
 * Schema-preview query key builder. The structured-data drawer's composed
 * JSON-LD graph + per-type ship status read from this cache. Anything that
 * mutates the authored-schema fields (article_description / howto edits) or
 * observes an async run flip to 'applied' should invalidate this key so the
 * drawer re-fetches the REAL publisher-composed graph.
 *
 * Exported because today nothing invalidates ['schema-preview', contentId]
 * and the authored-schema edits + run-complete transitions need to. Sibling
 * session C consumers (UniversalEditorRailMount debounced-save effect +
 * run→applied effect) import this helper instead of re-spelling the literal.
 */
export const SCHEMA_PREVIEW_KEY = (contentId: string) =>
	["schema-preview", contentId] as const;

// Statuses that indicate active generation (should trigger polling)
// Note: 'in_progress' is the main status during section-based generation
const IN_PROGRESS_STATUSES = [
	"pending",
	"in_progress",
	"analyzing_serp",
	"generating_brief",
	"writing_content",
	"validating",
	"publishing",
] as const;

// Polling interval when items are in progress (15 seconds — reduced from 8s to avoid
// flooding the backend during generation when the event loop is under load)
const PROGRESS_POLL_INTERVAL = 15_000;
// Backoff interval when the last fetch errored (prevents hammering an overloaded backend)
const ERROR_BACKOFF_INTERVAL = 30_000;

// Hook for listing all content
export const useContentList = (filters?: ContentFilters) => {
	const tenantId = useTenantStore((state) => state.currentTenant);
	return useQuery({
		queryKey: [QUERY_KEYS.content, tenantId, filters],
		// Pass abort signal so React Query can cancel in-flight requests on refetch/unmount
		queryFn: ({ signal }) => blogCreationService.list(filters, signal),
		staleTime: 5000, // 5s stale time — prevents rapid re-fetches on re-render
		gcTime: 30000, // Keep unused data in cache for 30 seconds
		refetchOnMount: 'always', // Always refetch on mount (catches newly created content after navigation)
		retry: 2, // Retry twice on failure (not 3 — reduces pileup on overloaded backend)
		retryDelay: (attemptIndex) => Math.min(2000 * 2 ** attemptIndex, 15000), // 2s, 4s
		// Conditional polling with error backoff
		refetchInterval: (query) => {
			// If the last fetch failed, back off significantly to let the backend recover
			if (query.state.error) return ERROR_BACKOFF_INTERVAL;

			const items = query.state.data?.items;
			if (!items || items.length === 0) return false;

			// Check if any items are in an active generation state
			const hasInProgressItems = items.some((item) =>
				IN_PROGRESS_STATUSES.includes(item.status as (typeof IN_PROGRESS_STATUSES)[number])
			);

			return hasInProgressItems ? PROGRESS_POLL_INTERVAL : false;
		},
	});
};

// Hook for getting a single content item's details
export const useContent = (contentId: string | null) => {
	return useQuery({
		queryKey: [QUERY_KEYS.contentItem, contentId],
		queryFn: () => {
			if (!contentId) throw new Error("Content ID is required");
			return blogCreationService.get(contentId);
		},
		enabled: !!contentId,
		staleTime: 5000, // Prevent refetch within 5 seconds of last fetch
		gcTime: 30000, // Keep unused data in cache for 30 seconds
		refetchOnWindowFocus: false, // Don't refetch when window regains focus
		retry: 3,
		retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
		// Poll while content is generating — keeps detail page in sync with list page
		refetchInterval: (query) => {
			if (query.state.error) return ERROR_BACKOFF_INTERVAL;
			// Also poll while an async postgen metadata run is pending so the rail
			// indicator and refreshed fields land without a manual reload.
			const metaStatus = query.state.data?.metadata_generation_status;
			if (metaStatus === "queued" || metaStatus === "running") return 8000;
			const status = query.state.data?.status;
			if (!status) return false;
			return IN_PROGRESS_STATUSES.includes(status as (typeof IN_PROGRESS_STATUSES)[number])
				? PROGRESS_POLL_INTERVAL
				: false;
		},
	});
};

// Hook for getting quality report for a content item
export const useQualityReport = (contentId: string | null, options?: { enabled?: boolean }) => {
	return useQuery({
		queryKey: ["quality-report", contentId],
		queryFn: () => {
			if (!contentId) throw new Error("Content ID is required");
			return blogCreationService.getQualityReport(contentId);
		},
		enabled: !!contentId && (options?.enabled !== false),
		staleTime: 60000, // Cache for 1 minute (quality reports don't change often)
		gcTime: 300000, // Keep in cache for 5 minutes
		refetchOnWindowFocus: false,
		retry: 1, // Only retry once on failure
	});
};

/**
 * Schema preview — the REAL composed JSON-LD graph + per-type ship status +
 * per-field source provenance. Lazy: only fetches when the drawer is open
 * (`enabled: !!contentId && drawerOpen`). Once cached, the rail card can read
 * the summary from the cache even when the drawer is closed.
 *
 * Fail-soft: on error the hook returns the error payload (the endpoint returns
 * 200 with `error` set rather than throwing); the drawer renders an honest
 * "couldn't load" state. retry: 1 so a transient 500 doesn't hammer.
 */
export const useSchemaPreview = (
	contentId: string | null,
	drawerOpen: boolean,
) => {
	return useQuery({
		queryKey: SCHEMA_PREVIEW_KEY(contentId ?? '__no_content__'),
		queryFn: () => {
			if (!contentId) throw new Error("Content ID is required");
			return blogCreationService.getSchemaPreview(contentId);
		},
		enabled: !!contentId && drawerOpen,
		staleTime: 30_000, // 30s — short enough to reflect toggle changes on reopen
		gcTime: 5 * 60_000, // Keep cached for 5 min so the card summary persists after close
		refetchOnWindowFocus: false,
		retry: 1,
	});
};

/**
 * Invalidate the schema-preview query for one content item. Used by the
 * authored-schema save path (debounced edit → PATCH article_description /
 * howto → invalidate so the drawer re-fetches the REAL composed graph) and
 * by the run→applied transition (auto-fill: when a Family-E run finishes,
 * the composed graph + field provenance need to refresh).
 *
 * The hook is intentionally tiny — it just wraps invalidateQueries with the
 * canonical key — so callers don't re-spell `['schema-preview', contentId]`
 * across the rail mount. Returns a stable callback.
 */
export const useInvalidateSchemaPreview = () => {
	const queryClient = useQueryClient();
	return useCallback(
		(contentId: string) => {
			queryClient.invalidateQueries({ queryKey: SCHEMA_PREVIEW_KEY(contentId) });
		},
		[queryClient],
	);
};

// Hook for getting generation activity log (polls every 10s when content is in progress)
export const useGenerationLog = (contentId: string | null, options?: { enabled?: boolean }) => {
	return useQuery({
		queryKey: ["generation-log", contentId],
		queryFn: ({ signal }) => {
			if (!contentId) throw new Error("Content ID is required");
			return blogCreationService.getGenerationLog(contentId, signal);
		},
		enabled: !!contentId && (options?.enabled !== false),
		staleTime: 5000,
		gcTime: 30000,
		refetchOnWindowFocus: false,
		retry: 1,
		refetchInterval: (query) => {
			if (query.state.error) return ERROR_BACKOFF_INTERVAL;
			const data = query.state.data;
			if (!data) return 10_000; // Poll while loading
			// Stop polling once completed or failed
			if (data.status === 'completed' || data.status === 'failed') return false;
			return 10_000; // Poll every 10 seconds while generating
		},
	});
};

// Hook for running quality analysis on content
export const useRunQualityAnalysis = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (contentId: string) => blogCreationService.runQualityAnalysis(contentId),
		onSuccess: (data, contentId) => {
			toast.success("Quality analysis completed");
			// Directly update the cache with the new report data
			// This avoids issues with HTTP caching on the GET endpoint
			queryClient.setQueryData(["quality-report", contentId], data);
		},
		onError: (error: Error) => {
			toast.error(`Analysis failed: ${error.message}`);
		},
	});
};

// Hook for getting judge report (30 rule checks + LLM critique)
export const useJudgeReport = (contentId: string | null, options?: { enabled?: boolean }) => {
	return useQuery({
		queryKey: ["judge-report", contentId],
		queryFn: () => {
			if (!contentId) throw new Error("Content ID is required");
			return blogCreationService.getJudgeReport(contentId);
		},
		enabled: !!contentId && (options?.enabled !== false),
		staleTime: 5 * 60 * 1000,
		gcTime: 300000,
		refetchOnWindowFocus: false,
		retry: 1,
	});
};

// Hook for refreshing judge report (force re-run)
export const useRefreshJudgeReport = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (contentId: string) => blogCreationService.getJudgeReport(contentId, true),
		onSuccess: (data, contentId) => {
			toast.success("Judge report refreshed");
			queryClient.setQueryData(["judge-report", contentId], data);
		},
		onError: (error: Error) => {
			toast.error(`Judge refresh failed: ${error.message}`);
		},
	});
};

// Hook for applying judge fixes to content
export const useApplyJudgeFixes = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (contentId: string) => blogCreationService.applyJudgeFixes(contentId),
		onSuccess: (data, contentId) => {
			const msg = data?.message || "Fixes applied — content updated";
			toast.success(msg);
			queryClient.invalidateQueries({ queryKey: ["judge-report", contentId] });
			queryClient.invalidateQueries({ queryKey: ["content", contentId] });
		},
		onError: (error: Error) => {
			toast.error(`Apply fixes failed: ${error.message}`);
		},
	});
};

// Hook for creating new content
export const useCreateContent = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateContentData) => blogCreationService.create(data),
		retry: 0, // Never retry creation — each attempt creates a new DB record
		onSuccess: (_, variables) => {
			if (variables.auto_start === false) {
				toast.success("Content added to queue");
			} else {
				toast.success("Content generation started");
			}
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.content] });
			// The planner table reads a different query key (['content-planner','calendar','multi',...]),
			// so the invalidation above never refreshes it. Prefix-match the calendar query so the
			// newly created row shows up after navigation back to the planner.
			queryClient.invalidateQueries({ queryKey: ['content-planner', 'calendar'] });
		},
		onError: (error: Error) => {
			// Missing Brand Bible (BRAND_BIBLE_REQUIRED marker): guide the user to
			// set one up instead of a dead-end error (2026-07-02 meeting promise).
			if (/BRAND_BIBLE_REQUIRED|Brand Bible/i.test(error.message)) {
				toast.error(
					"This workspace needs a Brand Bible before content can be generated.",
					{
						duration: 10000,
						action: {
							label: "Set up Brand Bible",
							onClick: () => window.location.assign("/brand-bible"),
						},
					},
				);
				return;
			}
			toast.error(`Failed to create content: ${error.message}`);
		},
	});
};

// Hook for starting pending content
export const useStartContent = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (contentId: string) => blogCreationService.start(contentId),
		onSuccess: (_, contentId) => {
			toast.success("Generation started");
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.content] });
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.contentItem, contentId],
			});
		},
		onError: (error: Error) => {
			toast.error(`Failed to start: ${error.message}`);
		},
	});
};

// Interface for SEO validation error response from backend
interface SEOValidationErrorDetail {
	error: string;
	errors: string[];
	warnings?: string[];
	keyword_density: number;
	meta_description_length: number;
	external_link_count: number;
}

// Hook for publishing content to WordPress
// Automatically fetches content_json and generates HTML before publishing
export const usePublishContent = () => {
	const queryClient = useQueryClient();
	const currentTenant = useTenantStore((state) => state.currentTenant);

	return useMutation({
		mutationFn: async (params: string | { contentId: string; builderNativeOverride?: boolean }) => {
			const operationTenant = currentTenant;
			const contentId = typeof params === 'string' ? params : params.contentId;
			const builderNativeOverride = typeof params === 'string' ? undefined : params.builderNativeOverride;

			// Fetch full content details to get content_json
			const detail = await blogCreationService.get(contentId, operationTenant);

			// Generate HTML from content_json (frontend is authoritative source)
			const htmlContent = detail.content_json
				? generateHTMLFromRTE(detail.content_json, operationTenant)
				: "";

			if (!htmlContent) {
				throw new Error("No content available to publish");
			}

			// publish_immediately=true: every usePublishContent surface
			// (BlogDetailPage, UnifiedBlogWorkspace) is a
			// manual "publish" action — going live is the intent. The old
			// draft behavior was a bug (backend defaults to draft when omitted).
			return blogCreationService.publish(
				contentId,
				htmlContent,
				true,
				builderNativeOverride,
				operationTenant,
			);
		},
		onSuccess: (_, params) => {
			const contentId = typeof params === 'string' ? params : params.contentId;
			toast.success("Blog post published to WordPress");
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.content] });
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.contentItem, contentId],
			});
		},
		onError: (error: unknown) => {
			// User chose "Go back" in the pre-publish metadata gate — abort silently.
			if (isPublishCancelledError(error)) return;
			// Check if this is an axios error with response details
			const axiosError = error as {
				response?: {
					status?: number;
					data?: { detail?: SEOValidationErrorDetail | string };
				};
				message?: string;
			};

			const detail = axiosError.response?.data?.detail;

			// 1. Handle string detail errors first (content not found, access denied, wrong status)
			if (typeof detail === "string") {
				toast.error(`Publish failed: ${detail}`, { duration: 6000 });
				return;
			}

			// 2. Handle SEO validation errors (400 status with structured detail)
			if (
				axiosError.response?.status === 400 &&
				detail &&
				typeof detail === "object" &&
				"errors" in detail
			) {
				const seoDetail = detail as SEOValidationErrorDetail;

				// Show specific error based on which validation failed
				// Backend uses 2.0% threshold for keyword density
				if (seoDetail.keyword_density > 2.0) {
					toast.error(
						`SEO Validation Failed: Keyword density is ${seoDetail.keyword_density.toFixed(2)}% (max 2.0%). Please reduce keyword repetitions before publishing.`,
						{ duration: 8000 }
					);
				} else if (
					seoDetail.meta_description_length < 150 ||
					seoDetail.meta_description_length > 155
				) {
					toast.error(
						`SEO Validation Failed: Meta description is ${seoDetail.meta_description_length} characters (must be 150-155).`,
						{ duration: 8000 }
					);
				} else if (seoDetail.external_link_count < 2) {
					toast.error(
						`SEO Validation Failed: Only ${seoDetail.external_link_count} external links found (minimum 2 required).`,
						{ duration: 8000 }
					);
				} else {
					// Generic SEO error - show all errors from backend
					toast.error(
						`SEO Validation Failed: ${seoDetail.errors.join(". ")}`,
						{ duration: 8000 }
					);
				}

				// Also show warnings if any
				if (seoDetail.warnings && seoDetail.warnings.length > 0) {
					toast.warning(`Warnings: ${seoDetail.warnings.join(". ")}`, {
						duration: 6000,
					});
				}
				return;
			}

			// 3. Fallback to generic error
			const message = axiosError.message || "Unknown error";
			toast.error(`Failed to publish: ${message}`);
		},
	});
};

// Hook for unpublishing content
export const useUnpublishContent = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (contentId: string) => blogCreationService.unpublish(contentId),
		onSuccess: (_, contentId) => {
			toast.success("Blog post reverted to draft");
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.content] });
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.contentItem, contentId],
			});
		},
		onError: (error: Error) => {
			toast.error(`Failed to unpublish: ${error.message}`);
		},
	});
};

// Hook for regenerating a stage
export const useRegenerateContentStage = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			contentId,
			stage,
		}: {
			contentId: string;
			stage: ContentStage;
		}) => blogCreationService.regenerate(contentId, stage),
		onSuccess: (_, { contentId }) => {
			toast.success("Regeneration started");
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.content] });
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.contentItem, contentId],
			});
		},
		onError: (error: Error) => {
			toast.error(`Failed to regenerate: ${error.message}`);
		},
	});
};

// Hook for deleting content
export const useDeleteContent = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (contentId: string) => blogCreationService.delete(contentId),
		onSuccess: () => {
			toast.success("Content deleted");
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.content] });
		},
		onError: (error: Error) => {
			toast.error(`Failed to delete: ${error.message}`);
		},
	});
};

// Hook for batch deleting content
export const useBatchDeleteContent = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (contentIds: string[]) =>
			blogCreationService.batchDelete(contentIds),
		onSuccess: (result) => {
			toast.success(`Deleted ${result.deleted} item(s)`);
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.content] });
		},
		onError: (error: Error) => {
			toast.error(`Failed to delete: ${error.message}`);
		},
	});
};

// Hook for batch publishing content to WordPress
// Accepts items with pre-generated HTML from frontend
export const useBatchPublishContent = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ items, tenantId }: {
			items: Array<{ content_id: string; html_content: string }>;
			tenantId?: string | null;
		}) => blogCreationService.batchPublish(items, { tenantId }),
		onSuccess: (result) => {
			if (result.published > 0) {
				toast.success(`Published ${result.published} post(s) to WordPress`);
			}
			if (result.failed > 0) {
				toast.warning(`${result.failed} post(s) failed to publish`);
			}
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.content] });
		},
		onError: (error: Error) => {
			// User cancelled the pre-publish metadata gate — no error toast.
			if (isPublishCancelledError(error)) return;
			toast.error(`Failed to publish: ${error.message}`);
		},
	});
};

// Hook for updating content brief
export const useUpdateContentBrief = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			contentId,
			brief,
		}: {
			contentId: string;
			brief: Parameters<typeof blogCreationService.updateBrief>[1];
		}) => blogCreationService.updateBrief(contentId, brief),
		onSuccess: (_, { contentId }) => {
			toast.success("Brief updated");
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.contentItem, contentId],
			});
		},
		onError: (error: Error) => {
			toast.error(`Failed to update brief: ${error.message}`);
		},
	});
};

// Hook for updating content HTML
export const useUpdateContent = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			contentId,
			contentHtml,
		}: {
			contentId: string;
			contentHtml: string;
		}) => blogCreationService.updateContent(contentId, contentHtml),
		onSuccess: (_, { contentId }) => {
			toast.success("Content updated");
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.contentItem, contentId],
			});
		},
		onError: (error: Error) => {
			toast.error(`Failed to update content: ${error.message}`);
		},
	});
};

// Hook for triggering validation
export const useValidateContent = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (contentId: string) => blogCreationService.validate(contentId),
		onSuccess: (_, contentId) => {
			toast.success("Validation completed");
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.contentItem, contentId],
			});
		},
		onError: (error: Error) => {
			toast.error(`Validation failed: ${error.message}`);
		},
	});
};

/**
 * Hook for manually requesting search engine indexing
 */
export const useRequestIndexing = () => {
	return useMutation({
		mutationFn: (contentId: string) =>
			blogCreationService.requestIndexing(contentId),
		onSuccess: (result) => {
			if (result.google?.success || result.bing?.success) {
				const engines = [];
				if (result.google?.success) engines.push("Google");
				if (result.bing?.success) engines.push("Bing");
				toast.success(`Indexing requested: ${engines.join(", ")}`);
			} else {
				toast.warning("Indexing services not configured for this tenant");
			}
		},
		onError: (error: Error) => {
			toast.error(`Failed to request indexing: ${error.message}`);
		},
	});
};

// Combined hook for content detail page
export const useContentDetail = (contentId: string | null) => {
	const { data: content, isLoading, refetch } = useContent(contentId);
	const publishMutation = usePublishContent();
	const regenerateMutation = useRegenerateContentStage();
	const { data: bridgeData } = useBridgeStatus();
	const bridgeToggleMutation = useBridgeToggle();

	const bridgeEnabled = bridgeData?.bridge_enabled ?? false;

	const publish = useCallback(async () => {
		if (!contentId) return;
		// Pass the bridge override explicitly so the backend picks the publish path.
		// While BRIDGE_UI_ENABLED is false this always resolves to Legacy, including
		// for rows stored with use_bridge = true before Bridge was switched off.
		const useBridge = BRIDGE_UI_ENABLED && bridgeEnabled && content?.use_bridge
			? true
			: false;
		await publishMutation.mutateAsync({ contentId, builderNativeOverride: useBridge });
	}, [contentId, publishMutation, bridgeEnabled, content?.use_bridge]);

	const regenerateStage = useCallback(
		async (stage: ContentStage) => {
			if (!contentId) return;
			await regenerateMutation.mutateAsync({ contentId, stage });
		},
		[contentId, regenerateMutation],
	);

	const toggleBridge = useCallback(
		(newValue: boolean) => {
			if (!contentId) return;
			bridgeToggleMutation.mutate({ contentId, useBridge: newValue });
		},
		[contentId, bridgeToggleMutation],
	);

	return {
		content: content || null,
		isLoading,
		publish,
		regenerateStage,
		refetch,
		isPublishing: publishMutation.isPending,
		isRegenerating: regenerateMutation.isPending,
		bridgeEnabled,
		toggleBridge,
	};
};

// Combined hook for content management
export const useContentManagement = () => {
	const [filters, setFilters] = useState<ContentFilters>({
		page: 1,
		page_size: 15,
	});
	const { data, isLoading, refetch } = useContentList(filters);
	const createMutation = useCreateContent();

	const items = data?.items || [];

	const create = useCallback(
		async (contentData: CreateContentData): Promise<Content> => {
			return await createMutation.mutateAsync(contentData);
		},
		[createMutation],
	);

	// Return the refetch promise so callers can await success/failure and surface
	// feedback. Rethrow on error so callers' try/catch can show a toast instead of
	// leaking an "Uncaught (in promise)" rejection when the list query fails
	// (e.g. 403 for an admin viewing a tenant they are not a member of).
	const refresh = useCallback(async () => {
		const result = await refetch();
		if (result.error) {
			throw result.error;
		}
	}, [refetch]);

	return {
		items,
		isLoading,
		create,
		refresh,
		filters,
		setFilters,
		total: data?.total || 0,
		isCreating: createMutation.isPending,
	};
};

// =============================================================================
// Bridge Publishing Hooks
// =============================================================================

/** Check if bridge publishing is enabled for the current tenant.
 *  Fails silently — returns undefined (treated as false) if endpoint is unavailable.
 *  No retries: this is a feature flag check, not critical data. */
/**
 * Bridge publishing is switched off across the UI for now — every article goes out
 * via Legacy (standard WordPress publishing). The backend endpoints, the tenant
 * flag, and the `use_bridge` column are untouched; flipping BRIDGE_UI_ENABLED back
 * to true restores every operator-facing Bridge control at once.
 */
export const BRIDGE_UI_ENABLED = false;

export const useBridgeStatus = () => {
	return useQuery({
		queryKey: ["bridge-status", BRIDGE_UI_ENABLED],
		queryFn: async () =>
			BRIDGE_UI_ENABLED
				? blogCreationService.getBridgeStatus()
				: { bridge_enabled: false },
		staleTime: 5 * 60_000, // Cache for 5 minutes (feature flag rarely changes)
		gcTime: 10 * 60_000,
		retry: false, // Don't retry on 404 — avoids wasting requests when bridge is not configured
		refetchOnWindowFocus: false,
		refetchOnMount: false, // Use cached value across remounts within staleTime
	});
};

/** Toggle bridge publishing for a specific content item. */
export const useBridgeToggle = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ contentId, useBridge }: { contentId: string; useBridge: boolean }) =>
			blogCreationService.toggleBridge(contentId, useBridge),
		onMutate: async ({ contentId, useBridge }) => {
			// Cancel outgoing refetches so they don't overwrite our optimistic update
			await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.content] });

			// Snapshot current cache for rollback
			const previousData = queryClient.getQueriesData({ queryKey: [QUERY_KEYS.content] });

			// Optimistically update all content list queries
			queryClient.setQueriesData(
				{ queryKey: [QUERY_KEYS.content] },
				(old: { items?: Content[] } | undefined) => {
					if (!old?.items) return old;
					return {
						...old,
						items: old.items.map((item) =>
							item.id === contentId ? { ...item, use_bridge: useBridge } : item
						),
					};
				}
			);

			return { previousData };
		},
		onSuccess: (result) => {
			toast.success(`Switched to ${result.use_bridge ? "Bridge" : "Legacy"} publishing`);
			// Refetch to ensure server state is in sync
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.content] });
		},
		onError: (error: Error, _variables, context) => {
			// Rollback optimistic update on failure
			if (context?.previousData) {
				for (const [queryKey, data] of context.previousData) {
					queryClient.setQueryData(queryKey, data);
				}
			}
			toast.error(`Failed to toggle bridge: ${error.message}`);
		},
	});
};

export const useExportToGoogleDocs = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			contentId,
			tenantId,
		}: {
			contentId: string;
			tenantId: string;
		}) => blogCreationService.exportToGoogleDocs(contentId, tenantId),
		onSuccess: (data, { contentId }) => {
			toast.success("Exported to Google Docs", {
				description: "Your document is ready.",
				action: {
					label: "Open Doc",
					onClick: () => window.open(data.gdocs_url, "_blank"),
				},
			});
			// Invalidate content detail so gdocs_doc_url refreshes in the toolbar
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.contentItem, contentId],
			});
		},
		onError: (error: Error) => {
			toast.error("Google Docs export failed", {
				description: error.message,
			});
		},
	});
};
